import { Box, Divider, FlexProps, Text, VStack } from '@chakra-ui/react'
import { ThemeContext, ThemeContextData } from '@hb/react'
import {
  breastSurgeryOrProceduresFields,
  earlyMenopauseFields,
  endometriosisFields,
  FieldMapValue,
  gynAndSexualHistoryWithoutPregnanciesField,
  hasHadMammogramFields,
  hasHadPapSmearFields,
  immunizationsField,
  nonCancerousBreastConditionsFields,
  nonSexuallyTransmittedVaginalInfectionsFields,
  ovarianCystsFields,
  sexualBehaviorField,
  sexuallyTransmittedInfectionsFields,
  UploadProgress,
  uterineOrCervicalAbnormalitiesFields,
  uterineProlapseFields,
} from '@hb/shared'

import { FORM_ERROR } from 'final-form'
import { useState } from 'react'
import { ActionLog } from '../ActionLog'
import { CollapseError } from '../CollapseError'
import { CorrectionsProvider, ShareCorrectionsProvider } from '../DataView/context'
import { InnerForm } from '../forms/FinalForm/InnerForm'
import { SimpleFormFooter } from '../forms/FinalForm/SimpleForm'
import { FormElement } from '../forms/Input'
import { RenderStageProps } from '../forms/renderStage/types'
import { ConditionGroupView } from './ConditionGroupView'
import { PreviousPregnanciesInput } from './PreviousPregnanciesInput'

const themeData: ThemeContextData = {
  theme: 'detailed',
  placeholderAbove: true,
}

const formFooterFlexProps: FlexProps = {
  pb: 0,
}

export const GynHistoryStage = ({
  data,
  corrections,
  onSubmit,
  readOnly,
  onComplete,
  registerForm,
}: RenderStageProps) => {
  const { updatedOn, updatedBy, updatedByGroup } = data ?? {}

  const [uploads, setUploads] = useState<Record<string, UploadProgress> | null>(null)
  const [initData] = useState<FieldMapValue>(data ?? {})

  return (
    <ThemeContext.Provider value={themeData}>
      <CorrectionsProvider corrections={corrections ?? null}>
        <ShareCorrectionsProvider shared={corrections?.correctionsShared ?? null}>
          <InnerForm
            field={gynAndSexualHistoryWithoutPregnanciesField}
            data={initData}
            key="gyn-history-stage"
            setUploads={setUploads}
            closeOnSuccess
            onClose={onComplete}
            onSubmit={onSubmit}
            registerForm={registerForm}>
            {({ errors, submitErrors }) => {
              const body = (
                <>
                  <PreviousPregnanciesInput
                    onSubmit={(values, onUploadProgress, form, closeOnSuccess) =>
                      onSubmit(
                        values,
                        u => {
                          onUploadProgress(u)
                          setUploads(u)
                        },
                        form,
                        closeOnSuccess,
                      )
                    }
                  />
                  <Divider my={2} />
                  <Text fontWeight={500} color="gray.600" fontFamily="Open Sans" fontSize="lg">
                    GYN History
                  </Text>
                  <ConditionGroupView
                    stageId="gynAndSexualHistory"
                    basePath="gynHistory"
                    fieldGroup={ovarianCystsFields}
                  />
                  <ConditionGroupView
                    stageId="gynAndSexualHistory"
                    basePath="gynHistory"
                    fieldGroup={nonSexuallyTransmittedVaginalInfectionsFields}
                  />
                  <ConditionGroupView
                    stageId="gynAndSexualHistory"
                    basePath="gynHistory"
                    fieldGroup={sexuallyTransmittedInfectionsFields}
                  />
                  <ConditionGroupView
                    stageId="gynAndSexualHistory"
                    basePath="gynHistory"
                    fieldGroup={endometriosisFields}
                  />
                  <ConditionGroupView
                    stageId="gynAndSexualHistory"
                    basePath="gynHistory"
                    fieldGroup={uterineOrCervicalAbnormalitiesFields}
                  />
                  <ConditionGroupView
                    stageId="gynAndSexualHistory"
                    basePath="gynHistory"
                    fieldGroup={uterineProlapseFields}
                    noMedications
                  />
                  <ConditionGroupView
                    stageId="gynAndSexualHistory"
                    basePath="gynHistory"
                    fieldGroup={nonCancerousBreastConditionsFields}
                  />
                  <ConditionGroupView
                    stageId="gynAndSexualHistory"
                    basePath="gynHistory"
                    fieldGroup={earlyMenopauseFields}
                  />
                  <ConditionGroupView
                    stageId="gynAndSexualHistory"
                    basePath="gynHistory"
                    fieldGroup={hasHadMammogramFields}
                    noMedications
                  />
                  <ConditionGroupView
                    stageId="gynAndSexualHistory"
                    basePath="gynHistory"
                    fieldGroup={breastSurgeryOrProceduresFields}
                    noMedications
                  />
                  <ConditionGroupView
                    stageId="gynAndSexualHistory"
                    basePath="gynHistory"
                    fieldGroup={hasHadPapSmearFields}
                    noMedications
                  />
                  <Divider my={2} />
                  <Text fontWeight={500} color="gray.600" fontFamily="Open Sans" fontSize="lg">
                    Sexual Behavior
                  </Text>
                  <FormElement
                    root
                    listIndex={0}
                    active
                    disabled={!!readOnly}
                    showName={false}
                    name="sexualBehavior"
                    field={sexualBehaviorField}
                  />
                  <Divider my={2} />
                  <Text fontWeight={500} color="gray.600" fontFamily="Open Sans" fontSize="lg">
                    Immunizations
                  </Text>
                  <FormElement
                    root
                    listIndex={0}
                    active
                    disabled={!!readOnly}
                    showName={false}
                    name="immunizations"
                    field={immunizationsField}
                  />
                  {updatedBy || updatedOn || updatedByGroup ? (
                    <Box px={4} py={1}>
                      <ActionLog
                        action="Updated"
                        by={updatedBy}
                        on={updatedOn}
                        group={updatedByGroup}
                      />
                    </Box>
                  ) : null}
                </>
              )
              return (
                <VStack
                  borderRadius={4}
                  align="flex-start"
                  bg="gray.50"
                  width="100%"
                  height="100%"
                  spacing={0}
                  borderTop="1px solid #cdcdcd"
                  shadow="md">
                  <Box py={3} px={4} flex={1} minH="0" w="100%" overflowY="auto">
                    {body}
                  </Box>
                  <Box px={4}>
                    <CollapseError
                      style={{ paddingTop: '0' }}
                      error={errors?.[FORM_ERROR] || submitErrors?.[FORM_ERROR]}
                    />
                  </Box>
                  <SimpleFormFooter
                    uploads={uploads}
                    canSubmitClean
                    flexProps={formFooterFlexProps}
                    theme="detailed"
                    submitText="SAVE"
                    submitInvalidText="NEXT"
                    submitCleanText="NEXT"
                  />
                </VStack>
              )
            }}
          </InnerForm>
        </ShareCorrectionsProvider>
      </CorrectionsProvider>
    </ThemeContext.Provider>
  )
}
